import { TrashIcon } from '@heroicons/react/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Button,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@mui/joy';
import Input from '@mui/joy/Input';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod'; // Import Zod
import useBranches from '../../hooks/usebranches';
import useCategories from '../../hooks/useCategories';
import useSubjects from '../../hooks/useSubjects';
import useTopic from '../../hooks/useTopic';
import fileUploadHandler from '../../middleware/UploadFile';
import useDeleteTopicMutation from '../../mutations/deleteTopic';
import { editTopicService } from '../../redux/services/topicService';
import { FileUploadButton } from '../library/fileUploadButton';

const topicSchema = z.object({
  title: z.string().nonempty('Naam is verplicht'),
  category: z.string().nonempty('Categorie is verplicht'),
  subject: z.string().nonempty('Onderwerp is verplicht'),
  isPrivate: z.boolean(),
  description: z.string().nonempty('Beschrijving is verplicht'),
  bannerImage: z.any().nullable().optional(),
  branch: z.string().nonempty('Vestiging is verplicht'),
});

type State = z.infer<typeof topicSchema>;

const initialState: State = {
  title: '',
  category: '',
  subject: '',
  isPrivate: false,
  description: '',
  bannerImage: null,
  branch: '',
};

type Props = {
  handleShowModal: (_: boolean) => void;
  topicId?: string;
  isOpen: boolean;
};

const TopicModal = (props: Props) => {
  const { handleShowModal, topicId, isOpen } = props;
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { t } = useTranslation();
  const topicQuery = useTopic(topicId ?? '');
  const categoryQuery = useCategories();
  const branchesQuery = useBranches();
  const subjectQuery = useSubjects();
  const deleteTopicMutation = useDeleteTopicMutation();
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<State>({
    resolver: zodResolver(topicSchema),
    defaultValues: initialState,
  });

  const bannerImage = watch('bannerImage');

  useEffect(() => {
    const topic = topicQuery.data;
    if (!topic) return;
    // If editing, set all fields
    setValue('title', topic.title);
    setValue('category', topic.category);
    setValue('subject', topic.subject);
    setValue('isPrivate', topic.isPrivate);
    setValue('description', topic.description);
    setValue('branch', topic.branch);
    setValue('bannerImage', topic.bannerImage);
  }, [topicQuery.data, setValue]);

  const deleteTopicHandler = async () => {
    if (!topicId) return;
    setLoading(true);
    deleteTopicMutation.mutate({
      id: topicId,
    });
    setLoading(false);
    navigate('/topics');
  };

  const isEditing = !!topicId;

  const onSubmit = async (data: State) => {
    setLoading(true);

    try {
      // If file has been uploaded, overwrite existing banner image
      let bannerImage = null;
      if (data.bannerImage) {
        bannerImage = await fileUploadHandler(data.bannerImage);
      }
      const topicData = { ...data, bannerImage, id: topicId };

      await editTopicService(topicData.id, topicData);
      handleShowModal(false);
    } catch (error: any) {
      setFormError(JSON.stringify(error?.response.data));
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };

  if (
    !subjectQuery.isSuccess ||
    !categoryQuery.isSuccess ||
    !topicQuery.isSuccess ||
    !branchesQuery.isSuccess
  ) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={() => handleShowModal(false)}>
      <ModalDialog size="lg" minWidth={'30vw'}>
        <DialogTitle>Topic aanmaken</DialogTitle>
        <form className="flex-col space-y-2" onSubmit={handleSubmit(onSubmit)}>
          {/* Name */}
          <FormControl error={!!errors.title}>
            <FormLabel>Naam</FormLabel>
            <Input
              placeholder="Type hier de naam van het topic..."
              {...register('title')}
            />
            {errors.title && (
              <Typography color="danger">{errors.title.message}</Typography>
            )}
          </FormControl>

          {/* Branch select */}
          <FormControl error={!!errors.branch}>
            <FormLabel>Vestiging</FormLabel>
            <Select
              placeholder="Vestiging"
              value={watch('branch')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('branch', newValue);
                } else {
                  setValue('branch', '');
                }
              }}
            >
              {branchesQuery.data.map((branch) => (
                <Option key={branch.name} value={branch.name}>
                  {branch.name}
                </Option>
              ))}
            </Select>
            {errors.branch && (
              <Typography color="danger">{errors.branch.message}</Typography>
            )}
          </FormControl>

          {/* Category dropdown */}
          <FormControl error={!!errors.category}>
            <FormLabel>Categorie</FormLabel>
            <Select
              placeholder="Categorie"
              value={watch('category')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('category', newValue);
                } else {
                  setValue('category', '');
                }
              }}
            >
              {categoryQuery.data.map((category: any) => (
                <Option key={category.label} value={category.label as string}>
                  {category.label}
                </Option>
              ))}
            </Select>
            {errors.category && (
              <Typography color="danger">{errors.category.message}</Typography>
            )}
          </FormControl>

          {/* Subject dropdown */}
          <FormControl error={!!errors.subject}>
            <FormLabel>Onderwerp</FormLabel>
            <Select
              placeholder="Onderwerp"
              value={watch('subject')}
              onChange={(e, newValue) => {
                if (typeof newValue === 'string') {
                  setValue('subject', newValue);
                } else {
                  setValue('subject', '');
                }
              }}
            >
              {subjectQuery.data.map((subject: any) => (
                <Option key={subject.label} value={subject.label as string}>
                  {subject.label}
                </Option>
              ))}
            </Select>
            {errors.subject && (
              <Typography color="danger">{errors.subject.message}</Typography>
            )}
          </FormControl>

          {/* Description */}
          <FormControl error={!!errors.description}>
            <FormLabel>Korte beschrijving</FormLabel>
            <Textarea minRows={2} {...register('description')} />
            {errors.description && (
              <Typography color="danger">
                {errors.description.message}
              </Typography>
            )}
          </FormControl>

          {/* File Upload */}
          <FormControl error={!!errors.description}>
            <FormLabel>Omslagfoto</FormLabel>
            <FileUploadButton
              accept="image/*"
              onSelect={(file) => {
                setValue('bannerImage', file);
              }}
              disabled={!!bannerImage}
            />
            {!!bannerImage && (
              <Typography>{(bannerImage as File).name}</Typography>
            )}
          </FormControl>

          {/* Public private toggle */}
          <Typography
            component="label"
            endDecorator={
              <Switch
                sx={{ ml: 1 }}
                checked={watch('isPrivate')}
                onChange={(e) => {
                  setValue('isPrivate', e.target.checked);
                }}
              />
            }
          >
            Privé?
          </Typography>

          {/* Delete button */}
          <div className="flex justify-between gap-3">
            {isEditing && (
              <div className="flex items-center">
                {!confirmDelete ? (
                  <button
                    className="p-2 rounded-md text-white bg-grey-medium_light hover:bg-grey-medium flex space-x-2"
                    onClick={() => setConfirmDelete(!confirmDelete)}
                    disabled={loading}
                  >
                    <TrashIcon className="w-5" />
                    <p>{t('delete')}</p>
                  </button>
                ) : (
                  <div className="flex items-center">
                    <button
                      className="px-4 py-2 bg-white rounded-md text-red-600 hover:font-bold"
                      onClick={deleteTopicHandler}
                      disabled={loading}
                    >
                      {t('yes')}
                    </button>
                    <button
                      className="px-4 py-2 bg-whiterounded-md text-sky-dark disabled:bg-grey-medium hover:font-bold"
                      onClick={() => setConfirmDelete(!confirmDelete)}
                      disabled={loading}
                    >
                      {t('no')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Error */}
          {formError && <p className="text-red-500">{formError}</p>}

          <Stack direction={'row'} spacing={1}>
            <Button
              startDecorator={<CloseRoundedIcon />}
              onClick={() => {
                handleShowModal(false);
              }}
              color="neutral"
              variant="outlined"
            >
              Annuleren
            </Button>
            <Button
              startDecorator={<CheckBoxRoundedIcon />}
              type="submit"
              disabled={loading}
            >
              {isEditing ? 'Bijwerken' : 'Aanmaken'}
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};

export default TopicModal;
